import React, { useState, useMemo, useEffect } from 'react';
import {
    CheckCircle,
    AlertTriangle,
    Search,
    X
} from 'lucide-react';

const InfoBox: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
    <div className="bg-blue-100 p-4 rounded-lg shadow-md mb-4">
        <h2 className="text-xl font-semibold text-blue-800 mb-2">{title}</h2>
        <ul className="list-disc list-inside text-blue-900">
            {items.map((item, i) => (
                <li key={i}>{item}</li>
            ))}
        </ul>
    </div>
);

const SearchBar = ({ searchTerm, setSearchTerm }: { searchTerm: string; setSearchTerm: (term: string) => void }) => (
    <div className="relative max-w-md mx-auto mb-8">
        <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
                type="text"
                placeholder="Search industries, trades, or categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            {searchTerm && (
                <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                    <X className="w-5 h-5" />
                </button>
            )}
        </div>
    </div>
);

const HighlightText = ({ text, searchTerm }: { text: string; searchTerm: string }) => {
    if (!searchTerm) return <>{text}</>;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return (
        <>
            {parts.map((part, index) =>
                regex.test(part) ? (
                    <mark key={index} className="bg-yellow-200 px-1 rounded">
                        {part}
                    </mark>
                ) : (
                    part
                )
            )}
        </>
    );
};

const CoverageTable = ({
    data,
    searchTerm
}: {
    data: { id: string; category: string; items: { label: string; status: 'Accept' | 'Considered' }[] }[];
    searchTerm: string;
}) => {
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        
        return data.map(group => ({
            ...group,
            items: group.items.filter(item =>
                item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                group.category.toLowerCase().includes(searchTerm.toLowerCase())
            )
        })).filter(group => 
            group.items.length > 0 || 
            group.category.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);

    //if (searchTerm && filteredData.length === 0) {
    //    return (
    //        <div className="bg-neutral-100 p-8 rounded-lg shadow-md mb-6 text-center">
    //            <p className="text-gray-600">No coverage items found matching "{searchTerm}"</p>
    //        </div>
    //    );
    //}

    return (
        <div className="bg-neutral-100 p-4 rounded-lg shadow-md mb-6">
            {filteredData.map((group, i) => (
                <div id={group.id} key={i} className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        <HighlightText text={group.category} searchTerm={searchTerm} />
                    </h3>
                    <ul className="text-gray-700">
                        {group.items.map((item, j) => (
                            <li key={j} className="flex items-center gap-2">
                                <span className="text-gray-500 mr-2">.</span>
                                <span>
                                    <HighlightText text={item.label} searchTerm={searchTerm} />
                                </span>
                                {item.status === 'Accept' ? (
                                    <CheckCircle className="text-green-600 w-4 h-4" />
                                ) : (
                                    <AlertTriangle className="text-yellow-500 w-4 h-4" />
                                )}
                                <span className="ml-auto text-sm italic text-gray-500">{item.status}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};

const appetiteData = [
    {
        category: "Electrical and Electronic",
        target: ["Audio Visual Engineers", "Electrical Equipment Manufacture / Wholesale / Supply"],
        writeWithCaution: [
            "Acoustic Engineers",
            "Broadband Provider / Mobile Phone Supplier",
            "Computer Consultants & Engineers"
        ],
        extremeCaution: [],
        decline: ["Auto Electricians"]
    },
    {
        category: "Catering & Leisure",
        target: [
            "Hairdressers", "Caf\u00E9 / Sandwich Shop", "Confectioners", "Restaurants", "Supermarkets",
            "Tattooists", "Grocers"
        ],
        writeWithCaution: [
            "Sports / Social Club", "Bakery", "Beauticians", "Butchers", "Caterers", "Event Organiser / Party Planner",
            "Garden Centres", "Hotels / Guest Houses", "Takeaways", "Cash & Carry", "Food / Drink Distributors",
            "Food Processing / Preparation"
        ],
        extremeCaution: [
            "Amusement Arcade", "Aertexes", "Billiards & Snooker Club", "Brewery / Public Houses",
            "Care Homes", "Gymnasiums", "Theatres (up to 2,000 seats)"
        ],
        decline: [
            "Actors", "Adult Club / Shop", "Airsoft / Paintball Operators", "Colleges / Adult Education",
            "Dog Groomers", "Drama Club", "Gambling Activities", "Entertainment Activities",
            "Holiday Homes / Campsites", "Nightclubs", "Public Attractions / Theme Parks", "Saunas",
            "Shooting Clubs", "Tanning Studios"
        ]
    },
    {
        category: "Clothing & Textiles",
        target: [
            "Carpet Fitters / Retails / Wholesale",
            "Curtain & Blind Fitters / Retail / Manufacturers",
            "Footwear Manufacture / Wholesale / Retail",
            "Tailors / Clothing Manufacture / Wholesale / Retail"
        ],
        writeWithCaution: ["Carpet Manufacture"],
        extremeCaution: ["Upholsterers", "French Polishers & Upholsterers"],
        decline: ["Textile Mills"]
    },
    {
        category: "Glass, Pottery, Bricks & Crafts",
        target: ["Builders / Bricklayers", "Concrete Goods Manufacture"],
        writeWithCaution: ["Glass Manufacture", "Monumental Masons / Stone Masons"],
        extremeCaution: ["Artists / Sculptures / Potters"],
        decline: ["Curtain Walling", "Jewellery Manufacture / Retail", "Paint Manufacture"]
    },
    {
        category: "Metals & Manufacturing",
        target: [
            "Bathroom fitter / installer / wholesaler",
            "Engravers",
            "Pedal Cycle Mechanic / Repairers",
            "Locksmiths / Key Cutters / Shoe Repair",
            "Plumbing & Heating",
            "Precision Engineers",
            "Sign Makers"
        ],
        writeWithCaution: [
            "Air Conditioning / Refrigeration Engineers",
            "Metal Fabrication (excl. steel)"
        ],
        extremeCaution: [],
        decline: [
            "Machinery Movement / Dismantling / Installation",
            "Powder Coaters",
            "Scrap metal manufacture / recycling",
            "Steel Fabrication"
        ]
    },
    {
        category: "Plastics",
        target: [
            "Double Glazing Retailers / Wholesale / Installers",
            "UPVC Window & Door Manufacture / Wholesale"
        ],
        writeWithCaution: [],
        extremeCaution: ["Toy Retail / Wholesale", "Plastic Product Manufacture"],
        decline: ["Plastic Extrusion / Moulding / Recycling", "Toy Manufacture"]
    },
    {
        category: "Printing & Paper",
        target: ["Printers"],
        writeWithCaution: [],
        extremeCaution: ["Art / Craft Manufacturers", "Cardboard Box / Paper Manufacturers"],
        decline: ["Paper Recycling"]
    },
    {
        category: "Wood",
        target: [
            "Fitted Furniture Retail / Wholesale / Manufacture",
            "Carpenters / Joiners",
            "Kitchen Manufacture / Wholesale / Retail / Installation",
            "Shelving & Racking Manufacture / Wholesale / Retail",
            "Shopfitters"
        ],
        writeWithCaution: ["Wooden Goods Manufacture"],
        extremeCaution: [],
        decline: ["Sawmills / Timber Merchants", "Pallet Manufacture"]
    },
    {
        category: "Miscellaneous",
        target: ["Offices", "Retailers (no 100% online)"],
        writeWithCaution: ["Christmas Decoration Installers / Retailers / Manufacture"],
        extremeCaution: ["Fancy Goods Manufacture / Wholesale / Retail"],
        decline: ["Baby Carriages Retail / Wholesale / Manufacture", "Car Sales"]
    }
];

const AppetiteTable = ({ searchTerm }: { searchTerm: string }) => {
    const filteredData = useMemo(() => {
        if (!searchTerm) return appetiteData;
        
        return appetiteData.map(item => {
            const matchesCategory = item.category.toLowerCase().includes(searchTerm.toLowerCase());
            
            return {
                ...item,
                target: item.target.filter(t => t.toLowerCase().includes(searchTerm.toLowerCase())),
                writeWithCaution: item.writeWithCaution.filter(t => t.toLowerCase().includes(searchTerm.toLowerCase())),
                extremeCaution: item.extremeCaution.filter(t => t.toLowerCase().includes(searchTerm.toLowerCase())),
                decline: item.decline.filter(t => t.toLowerCase().includes(searchTerm.toLowerCase())),
                hasMatches: matchesCategory || 
                    item.target.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    item.writeWithCaution.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    item.extremeCaution.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    item.decline.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()))
            };
        }).filter(item => !searchTerm || item.hasMatches);
    }, [searchTerm]);

    if (searchTerm && filteredData.length === 0) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-600 text-lg">No appetite categories found matching "{searchTerm}"</p>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-8">
            {filteredData.map((item, index) => (
                <div key={index} className="border border-gray-300 rounded-xl shadow-sm overflow-hidden">
                    <h2 className="text-xl font-semibold bg-gray-100 p-4">
                        <HighlightText text={item.category} searchTerm={searchTerm} />
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-y md:divide-y-0">
                        <Column title="Target" items={item.target} searchTerm={searchTerm} />
                        <Column title="Write With Caution" items={item.writeWithCaution} searchTerm={searchTerm} />
                        <Column title="Extreme Caution" items={item.extremeCaution} searchTerm={searchTerm} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const Column = ({ title, items, searchTerm }: { title: string; items: string[]; searchTerm: string }) => (
    <div className="p-4">
        <h3 className="text-lg font-medium mb-2">{title}</h3>
        <ul className="list-disc list-inside space-y-1 text-sm">
            {items.length > 0 ? (
                items.map((item, idx) => (
                    <li key={idx}>
                        <HighlightText text={item} searchTerm={searchTerm} />
                    </li>
                ))
            ) : (
                <li className="italic text-gray-500">
                    {searchTerm ? 'No matching items' : 'None'}
                </li>
            )}
        </ul>
    </div>
);

const InsuranceInfoPage = () => {

    const [searchTerm, setSearchTerm] = useState('');

    // Handle hash navigation when component mounts and when hash changes
    useEffect(() => {

        const hash = window.location.hash;

        if (hash) {
            const sectionId = hash.substring(1);

            setTimeout(() => {
                const element = document.getElementById(sectionId);

                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 300);
        }
    }, []);

    return (
        <>
            <section className="bg-pink-900 py-20 px-4">
                <div className="max-w-7xl mx-auto text-center text-white">
                    <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">Our Insurance Services</h1>
                    <p className="text-xl max-w-3xl mx-auto">
                        Comprehensive insurance solutions designed to protect what matters most.
                    </p>
                </div>
            </section>

            <section className="py-16 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-heading font-semibold text-neutral-800 mb-4">
                            Find the Perfect Coverage for Your Client Needs
                        </h2>
                        <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                            At Axent Brokers, we offer a wide range of insurance products to protect your clients, and their businesses.
                            Explore our services below or contact us for personalised assistance.
                        </p>
                    </div>

                    <div id="residentialpropertyowners" className="mb-40">
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-3xl font-heading font-semibold text-neutral-800 mb-4">
                            Residential Property Owners' Insurance
                        </h2>
                        <InfoBox
                            title="We can offer"
                            items={[
                                'Flexible underwriting approach',
                                'Suitable for risks with up to \u00A32m Buildings Sum Insured',
                                'Covers include Buildings, Landlord Contents, Rental Income and Property Liability',
                                'Cover for Long term unoccupied, Asylum Seekers, Listed properties, non-standard construction',
                                'Multi-Premises Cover',
                                'Customizable deductibles and coverage limits',
                                'Acceptable Unoccupied Properties Detached/Terraced Houses, Flats',
                                'Considered Unoccupied Flat above non-shop Commercial',
                                'Premiums start from \u00A3132 per annum inclusive of IPT and fees'
                            ]}
                        />
                    </div>


                    <div id="commercialpropertyowners" className="mb-25">
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-3xl font-heading font-semibold text-neutral-800 mb-4">
                            Commercial Property Owners' Insurance
                        </h2>
                        <InfoBox
                            title="We can offer"
                            items={[
                                'Flexible underwriting approach',
                                'Suitable for risks with up to \u00A32m Buildings Sum Insured',
                                'Covers include Buildings, Landlord Contents, Rental Income and Property Liability',
                                'Cover for Long term unoccupied, Listed properties, non-standard construction',
                                'Multi-Premises Cover',
                                'Customizable deductibles and coverage limits',
                                'Acceptable Unoccupied Properties Office, Shop, Warehouse',
                                'Considered Unoccupied Hospital, Manufacturing',
                                'Premiums start from \u00A3254 per annum inclusive of IPT and fees'
                            ]}
                        />
                    </div>

                    <div id="commercialcombined" className="mb-25">
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-3xl font-heading font-semibold text-neutral-800 mb-4">
                            Commercial Combined Insurance
                        </h2>
                        <InfoBox
                            title="We can offer"
                            items={[
                                'Tailored cover for SMEs across multiple sectors',
                                'Flexible underwriting approach',
                                'Suitable for risks with up to \u00A35m turnover',
                                'Covers include Property, Liability, Business Interruption, and more',
                            ]}
                        />

                        <InfoBox
                            title="Coverages"
                            items={[
                                'Buildings, Contents, Stock',
                                'Business Interruption (Revenue/Profit)',
                                'Employers\' Liability, Public & Products Liability',
                                'Legal Expenses, Money & Assault',
                                'Goods in Transit, Loss of Licence, and more',
                            ]}
                        />

                        <InfoBox
                            title="Key Limits & Flexibility"
                            items={[
                                '\u00A35m Material Damage/Business Interruption exposure',
                                '\u00A310m Employers\'/Public/Products Liability',
                                '\u00A34m maximum building sum insured',
                                'Turnover up to \u00A35m',
                                'Worldwide cover (subject to criteria)',
                            ]}
                        />

                        <InfoBox
                            title="Special Considerations"
                            items={[
                                'North American Products Cover (subject to referral)',
                                'Non-standard construction and composite panels',
                                'Grade Listed properties (excluding Grade I/A)',
                                'Clients with adverse financial history or ex-offenders',
                            ]}
                        />

                        <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

                        <CoverageTable
                            searchTerm={searchTerm}
                            data={[
                                {
                                    id: 'commercialcombined',
                                    category: 'Commercial Combined',
                                    items: [
                                        { label: 'Wholesale: Food, Beverage, Electronics', status: 'Accept' },
                                        { label: 'Retail: Bookstores, Clothing, Pharmacies', status: 'Accept' },
                                        { label: 'Office: Accountants, Lawyers, Software Devs', status: 'Accept' },
                                        { label: 'Toy/Pet/Office Supply', status: 'Considered' },
                                    ],
                                },
                                {
                                    id: 'trade',
                                    category: 'Leisure / Manufacturing / Food & Drink',
                                    items: [
                                        { label: 'Leisure: Gyms, Theatres, Cinemas', status: 'Accept' },
                                        { label: 'Manufacturing: Food, Clothing', status: 'Accept' },
                                        { label: 'Food/Drink: Bakeries, Catering', status: 'Accept' },
                                        { label: 'Theme Parks, Toy Manufacturing', status: 'Considered' },
                                    ],
                                },
                                {
                                    id: 'propertyowners',
                                    category: 'Property Owners / Contractors / Warehouse',
                                    items: [
                                        { label: 'Agricultural: Gardeners, Tree Surgeons', status: 'Accept' },
                                        { label: 'Cleaning: Window Cleaners, Car Valets', status: 'Accept' },
                                        { label: 'Contractors: Painters, Kitchen Fitters', status: 'Accept' },
                                        { label: 'Lawn Treatment, Pest Control', status: 'Considered' },
                                        { label: 'Contractors: Plumbers, Painters', status: 'Accept' },
                                        { label: 'Warehouse: Clothing, Furniture', status: 'Accept' },
                                        { label: 'Community: Charities, Youth Groups', status: 'Accept' },
                                        { label: 'Taxi Operators, Adult Education', status: 'Considered' },
                                    ],
                                },
                            ]}
                        />
                    </div>

                    <div className="space-y-6">
                        <AppetiteTable searchTerm={searchTerm} />
                    </div>
                </div>
            </section>
        </>
    );
};

export default InsuranceInfoPage;